import React from "react";
import { Reveal } from "../components/Reveal";
import { Link } from "react-router-dom";
import { EMAIL, PHONE_DISPLAY, WHATSAPP_NUMBER } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";
import { usePageMeta } from "../hooks/usePageMeta";

const Impressum = () => {
  const { t } = useLanguage();
  usePageMeta(t.meta.impressum);
  return (
    <div data-testid="impressum-page" className="bg-white">
      <section className="pt-40 md:pt-48 pb-16 md:pb-24 px-6 md:px-12">
        <div className="max-w-[900px] mx-auto">
          <Reveal>
            <p className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/50 mb-6">
              — Rechtliche Hinweise
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-serif-aw text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.05]">
              Impressum
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-32 md:pb-40">
        <div className="max-w-[900px] mx-auto space-y-14">
          {/* Betreiber */}
          <Reveal delay={0.1}>
            <div className="border-t border-black pt-8">
              <h2 className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 mb-5">
                Angaben gemäss Schweizer Recht
              </h2>
              <address className="not-italic font-serif-aw text-xl md:text-2xl text-black leading-relaxed">
                Alpha&nbsp;–&nbsp;Watch Kristian Yukhan<br />
                Brandschenkestrasse 6<br />
                8001 Zürich<br />
                Schweiz
              </address>
              <dl className="mt-8 font-sans-aw text-sm md:text-base text-black/80 space-y-3">
                <div className="flex gap-6">
                  <dt className="w-40 text-black/50 shrink-0">UID / MwSt-Nr.</dt>
                  <dd data-testid="imprint-uid">CHE-245.727.128</dd>
                </div>
                <div className="flex gap-6">
                  <dt className="w-40 text-black/50 shrink-0">Telefon</dt>
                  <dd>
                    <a href={`tel:+${WHATSAPP_NUMBER}`} className="link-aw">{PHONE_DISPLAY}</a>
                  </dd>
                </div>
                <div className="flex gap-6">
                  <dt className="w-40 text-black/50 shrink-0">E-Mail</dt>
                  <dd>
                    <a href={`mailto:${EMAIL}`} className="link-aw break-all">{EMAIL}</a>
                  </dd>
                </div>
              </dl>
            </div>
          </Reveal>

          {/* Verantwortlich */}
          <Reveal delay={0.15}>
            <div className="border-t border-black/15 pt-8">
              <h2 className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 mb-4">
                Verantwortlich für den Inhalt
              </h2>
              <p className="font-sans-aw text-base text-black/80 leading-relaxed">
                Kristian Yukhan, Brandschenkestrasse 6, 8001 Zürich, Schweiz
              </p>
            </div>
          </Reveal>

          {/* Haftung */}
          <Reveal delay={0.2}>
            <div className="border-t border-black/15 pt-8">
              <h2 className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 mb-4">
                Haftungsausschluss
              </h2>
              <p className="font-sans-aw text-base text-black/80 leading-relaxed">
                Der Autor übernimmt keinerlei Gewähr hinsichtlich der inhaltlichen Richtigkeit,
                Genauigkeit, Aktualität, Zuverlässigkeit und Vollständigkeit der Informationen.
                Haftungsansprüche gegen den Autor wegen Schäden materieller oder immaterieller Art,
                welche aus dem Zugriff oder der Nutzung bzw. Nichtnutzung der veröffentlichten
                Informationen, durch Missbrauch der Verbindung oder durch technische Störungen
                entstanden sind, werden ausgeschlossen.
              </p>
            </div>
          </Reveal>

          {/* Links */}
          <Reveal delay={0.25}>
            <div className="border-t border-black/15 pt-8">
              <h2 className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 mb-4">
                Haftung für Links
              </h2>
              <p className="font-sans-aw text-base text-black/80 leading-relaxed">
                Verweise und Links auf Webseiten Dritter liegen ausserhalb unseres
                Verantwortungsbereichs. Es wird jegliche Verantwortung für solche Webseiten abgelehnt.
                Der Zugriff und die Nutzung solcher Webseiten erfolgen auf eigene Gefahr des Nutzers.
              </p>
            </div>
          </Reveal>

          {/* Urheberrecht */}
          <Reveal delay={0.3}>
            <div className="border-t border-black/15 pt-8">
              <h2 className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 mb-4">
                Urheberrechte
              </h2>
              <p className="font-sans-aw text-base text-black/80 leading-relaxed">
                Die Urheber- und alle anderen Rechte an Inhalten, Bildern, Fotos oder anderen Dateien
                auf dieser Website gehören ausschliesslich der Firma Alpha&nbsp;–&nbsp;Watch Kristian Yukhan
                oder den speziell genannten Rechteinhabern. Für die Reproduktion jeglicher Elemente
                ist die schriftliche Zustimmung der Urheberrechtsträger im Voraus einzuholen.
              </p>
            </div>
          </Reveal>

          {/* Datenschutz kurz */}
          <Reveal delay={0.35}>
            <div className="border-t border-black/15 pt-8">
              <h2 className="font-sans-aw text-[10px] tracking-luxury uppercase text-black/60 mb-4">
                Datenschutz
              </h2>
              <p className="font-sans-aw text-base text-black/80 leading-relaxed">
                Informationen zur Bearbeitung Ihrer Personendaten, zu Cookies und zu den eingesetzten
                Diensten finden Sie in unserer{" "}
                <Link data-testid="imprint-privacy-link" to="/datenschutz" className="link-aw underline underline-offset-4">
                  Datenschutzerklärung
                </Link>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Impressum;
